import { useContext, useState } from 'react';

import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { getCareMatrixProfile } from 'utils/helpers/careMatrix';

import { SpecialistContext } from 'context';
import { useCareMatrix } from 'hooks/useCareMatrix';
import {
  getEndUserActivitiesHistory,
  getEndUserCareMatrixHistory,
  getEndUserProgramsHistory,
} from 'services/user';

import { EmptyState } from 'components/CareMatrixModule/components/EmptyState';
import ListItem from 'components/CareMatrixModule/components/List/ListItem';

import Topic from '../../components/Topic';

import * as S from './styles';

export function History() {
  const intl = useIntl();
  const { patientCognitoId } = useCareMatrix();
  const [expanded, setExpanded] = useState<string | false>(false);
  const { specialistInfo } = useContext(SpecialistContext);

  const { data: careMatrixHistory, isLoading: isLoadingCareMatrix } = useQuery({
    queryFn: () => getEndUserCareMatrixHistory(patientCognitoId),
    queryKey: ['getEndUserCareMatrixHistory', patientCognitoId],
  });

  const { data: activitiesHistory, isLoading: isLoadingActivities } = useQuery({
    queryFn: () => getEndUserActivitiesHistory(patientCognitoId),
    queryKey: ['getEndUserActivitiesHistory', patientCognitoId],
  });

  const { data: programsHistory, isLoading: isLoadingPrograms } = useQuery({
    queryFn: () => getEndUserProgramsHistory(patientCognitoId),
    queryKey: ['getEndUserProgramsHistory', patientCognitoId],
  });

  function handleExpandPanel(panel: string) {
    // eslint-disable-next-line func-names
    return function (_: React.SyntheticEvent, isExpanded: boolean) {
      setExpanded(isExpanded ? panel : false);
    };
  }

  return (
    <Box>
      <Box pt={3} pb={4} px={3} display="flex" flexDirection="column" gap={3}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="h6">
            {intl.formatMessage({
              id: 'CAREMATRIXCPTC.PROGRAMCMTITLE.CAREPLAN',
            })}
          </Typography>

          <Typography variant="body2">
            {intl.formatMessage(
              {
                id: 'CAREMATRIXCPTC.HISTORYCMSUBTITLE.CAREPLAN',
              },
              {
                date: new Date().toLocaleDateString(intl.locale, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }),
              },
            )}
          </Typography>

          {isLoadingCareMatrix ? (
            <Box mt={1}>
              <Skeleton height={80} width={386} variant="rounded" />
            </Box>
          ) : (
            <Box mt={1}>
              {!!careMatrixHistory?.length &&
                careMatrixHistory.map(each => {
                  const isExpanded = expanded === `topic-${each.title_key}`;

                  return (
                    <Accordion
                      key={each.title_key}
                      expanded={isExpanded}
                      onChange={handleExpandPanel(`topic-${each.title_key}`)}
                    >
                      <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                        <Box display="inline-block">
                          <Typography variant="body1">
                            {each.title_key}
                          </Typography>
                          <Chip
                            label={intl.formatMessage({
                              id: getCareMatrixProfile(each.care_matrix_profile)
                                ?.profile_name,
                            })}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <S.AccordionDetails>
                        {each.topics?.map(topic => {
                          return (
                            <Accordion key={topic.id}>
                              <AccordionSummary
                                expandIcon={<KeyboardArrowDown />}
                              >
                                <Typography variant="body1">
                                  {topic.title_key}
                                </Typography>
                              </AccordionSummary>
                              <S.AccordionDetails>
                                <Topic
                                  open={isExpanded}
                                  cognitoId={patientCognitoId}
                                  topicId={topic.id}
                                />
                              </S.AccordionDetails>
                            </Accordion>
                          );
                        })}

                        <ListItem>
                          <ListItemText>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              textAlign="center"
                            >
                              {intl.formatMessage(
                                {
                                  id: 'CAREMATRIXCPTC.HISTORYTAB.RECOMMENDATIONCM',
                                },
                                {
                                  date: new Date(
                                    each.deliver_date,
                                  ).toLocaleDateString(intl.locale, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  }),
                                },
                              )}
                            </Typography>
                          </ListItemText>
                        </ListItem>
                      </S.AccordionDetails>
                    </Accordion>
                  );
                })}

              {!careMatrixHistory?.length && (
                <EmptyState
                  text={intl.formatMessage({
                    id: 'CAREMATRIXCPTC.EMPTYSTATES.NOTFINISHED',
                  })}
                />
              )}
            </Box>
          )}
        </Box>

        {specialistInfo?.geoCode && specialistInfo.geoCode === 'BR' && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="h6">
              {intl.formatMessage({
                id: 'CAREMATRIXCPTC.ACTIVITIESTAB.TITLEACTIVITIES',
              })}
            </Typography>

            <Typography variant="body2">
              {intl.formatMessage({
                id: 'CAREMATRIXCPTC.HISTORYTABSUBTITLE.ACTIVITIESRECOMMENDED',
              })}
            </Typography>

            {isLoadingActivities ? (
              <Box mt={2} display="flex" flexDirection="column" gap={2}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    height={40}
                    width={386}
                    variant="rounded"
                  />
                ))}
              </Box>
            ) : (
              <Box mt={2} display="flex" flexDirection="column" gap={2}>
                {!!activitiesHistory?.results.length &&
                  activitiesHistory?.results.map(each => {
                    const isExpanded = expanded === `activity-${each.id}`;

                    return (
                      <Accordion
                        key={each.id}
                        expanded={isExpanded}
                        onChange={handleExpandPanel(`activity-${each.id}`)}
                      >
                        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                          <Typography variant="body1">
                            {each.titleKey}
                          </Typography>
                        </AccordionSummary>

                        <S.AccordionDetails>
                          <Topic
                            open={isExpanded}
                            cognitoId={patientCognitoId}
                            topicId={each.id}
                          />

                          <ListItem>
                            <ListItemText>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                textAlign="center"
                              >
                                {intl.formatMessage(
                                  {
                                    id: 'CAREMATRIXCPTC.ACTIVITIESTAB.SUBTITLEPROGRAMS',
                                  },
                                  {
                                    date: new Date(
                                      each.deliverDate,
                                    ).toLocaleDateString(intl.locale, {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                    }),
                                  },
                                )}
                              </Typography>
                            </ListItemText>
                          </ListItem>
                        </S.AccordionDetails>
                      </Accordion>
                    );
                  })}

                {!activitiesHistory?.results.length && (
                  <Alert variant="outlined" severity="info">
                    {intl.formatMessage({
                      id: 'CAREMATRIXCPTC.EMPTYSTATES.NOTACTIVITIES',
                    })}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {specialistInfo?.geoCode && specialistInfo.geoCode === 'BR' && (
        <Box pt={3} pb={4} px={3} bgcolor="secondary.main">
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="h6">
              {intl.formatMessage({
                id: 'CAREMATRIXCPTC.ACTIVITIESTAB.TITLEPROGRAMS',
              })}
            </Typography>

            <Typography variant="body2">
              {intl.formatMessage({
                id: 'CAREMATRIXCPTC.HISTORYTABSUBTITLE.ACTIVITIESRECOMMENDED',
              })}
            </Typography>

            {isLoadingPrograms ? (
              <Box mt={2} display="flex" flexDirection="column" gap={2}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    height={40}
                    width={386}
                    variant="rounded"
                  />
                ))}
              </Box>
            ) : (
              <Box mt={1} display="flex" flexDirection="column" gap={2}>
                {!!programsHistory?.length &&
                  programsHistory.map(each => {
                    const isExpanded =
                      expanded === `program-${each.program_id}`;

                    return (
                      <Accordion
                        key={each.program_id}
                        expanded={isExpanded}
                        onChange={handleExpandPanel(
                          `program-${each.program_id}`,
                        )}
                      >
                        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                          {each.title_key}
                        </AccordionSummary>
                        <S.AccordionDetails>
                          {each.topics?.map(topic => {
                            return (
                              <Accordion key={topic.id}>
                                <AccordionSummary
                                  expandIcon={<KeyboardArrowDown />}
                                >
                                  <Typography variant="body1">
                                    {topic.title_key}
                                  </Typography>
                                </AccordionSummary>
                                <S.AccordionDetails>
                                  <Topic
                                    open={isExpanded}
                                    cognitoId={patientCognitoId}
                                    topicId={topic.id}
                                  />

                                  <ListItem>
                                    <ListItemText>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        textAlign="center"
                                      >
                                        {intl.formatMessage(
                                          {
                                            id: 'CAREMATRIXCPTC.ACTIVITIESTAB.SUBTITLEPROGRAMS',
                                          },
                                          {
                                            date: new Date(
                                              each.deliver_date,
                                            ).toLocaleDateString(intl.locale, {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric',
                                            }),
                                          },
                                        )}
                                      </Typography>
                                    </ListItemText>
                                  </ListItem>
                                </S.AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </S.AccordionDetails>
                      </Accordion>
                    );
                  })}

                {!programsHistory?.length && (
                  <Alert variant="outlined" severity="info">
                    {intl.formatMessage({
                      id: 'CAREMATRIXCPTC.EMPTYSTATES.NOTPROGRAMS',
                    })}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
